import { Box, Drawer } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { fullSize } from '../../../Constants';
import Theme, { defaultBorder } from '../../../Theme';
import useBreakpoints from '../../../hooks/useBreakpoint';

/** Default "dragger" element width. */
const defaultDraggerWidth = 8;

/**
 * Customised MUI Drawer that can be resized by dragging and dropping a control element.
 */
export default ({
    children,
    minWidth,
    maxWidth,
    drawerWidth,
    draggerWidth,
}: IResizableDrawerProps) => {
    /** State slice that stores the width of the Drawer component. */
    const [width, setWidth] = useState(drawerWidth ?? minWidth);

    /** State slice that tracks if the user is resizing the Drawer. */
    const [dragging, setDragging] = useState(false);

    /**
     * Resets the dragging flag when the user releases the mouse button.
     */
    const handleMouseUp = () => setDragging(false);

    /** Determine screen size. */
    const { isMobile } = useBreakpoints();

    /**
     * Updates the size of the Drawer when the user is performing a resize.
     * @param e -> User-triggered MouseEvent.
     */
    const handleMouseMove = useCallback((e: MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();

        const newWidth = e.clientX - document.body.offsetLeft;

        if (newWidth > minWidth && newWidth < maxWidth) {
            setWidth(newWidth);
        }
    }, []);

    /**
     * Effect that adds/removes event handlers when the user starts/stops a resize.
     */
    useEffect(() => {
        const evt = dragging ? 'addEventListener' : 'removeEventListener';

        document[evt]('mouseup', handleMouseUp, true);
        document[evt]('mousemove', handleMouseMove, true);

        /** Prevent on-screen text from accidentally being selected while the resize is in progress. */
        document.body.style.userSelect = dragging ? 'none' : 'initial';
    }, [dragging]);

    /** Use the default width if one wasn't provided in the component's props. */
    const dim = draggerWidth ?? defaultDraggerWidth;

    /** Pseudo element and hover styles for the "dragger" element. */
    const draggerSx = {
        position: 'absolute',
        cursor: 'ew-resize',
        width: dim,
        bottom: 0,
        right: 0,
        top: 0,

        '&:after': {
            transition: 'background-color 0.1s ease-out',
            borderRight: defaultBorder,
            bgcolor: 'transparent',
            position: 'absolute',
            width: dim / 2,
            content: '""',
            bottom: 0,
            right: 0,
            top: 0,

            '&:hover': {
                bgcolor: Theme.palette.divider,
            },
        },
    };

    return (
        <Drawer
            variant='permanent'
            PaperProps={{ style: { width, position: 'relative', border: 0, overflow: 'unset' } }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    ...fullSize,
                    width: isMobile ? '95%' : '100%',
                }}
                children={children}
            />
            <Box sx={draggerSx} onMouseDown={() => setDragging(true)} />
        </Drawer>
    );
};

/**
 * Interface that describes the props for a ResizableDrawer component.
 */
export interface IResizableDrawerProps extends React.PropsWithChildren {
    /** The minimum allowed size of the Drawer. */
    minWidth: number;

    /** The maximum allowed size of the Drawer. */
    maxWidth: number;

    /** (Optional) width of the "dragger" element. */
    draggerWidth?: number;

    /** (Optional) Starting width of the Drawer (defaults is the value of "minWidth""). */
    drawerWidth?: number;
}
